import { cva } from 'class-variance-authority'
import { useMemo, useRef } from 'react'

import { useScheduledLayoutEffect } from 'slab/hooks/useScheduledLayoutEffect'

type Day = {
  ariaLabel: string;
  date: Date;
  dayOfMonth: number;
} | null

function isToday (date?: Date | null) {
  if (!date) return false

  const dateWithoutTime = new Date(date).setHours(0, 0, 0, 0)
  const todayWithoutTime = new Date().setHours(0, 0, 0, 0)

  return dateWithoutTime === todayWithoutTime
}

function isSameDate (
  date1?: Date | string | null,
  date2?: Date | string | null
) {
  if (!date1 || !date2) return false

  const date1WithoutTime = new Date(date1).setHours(0, 0, 0, 0)
  const date2WithoutTime = new Date(date2).setHours(0, 0, 0, 0)

  return date1WithoutTime === date2WithoutTime
}

/**
 * Gets the number of days in any given month in any given year by leveraging
 * the fact that Javascript `Date` objects automatically "roll over" dates when
 * you provide month or date values in the constructor that are out-of-bounds.
 *
 * For example, constructing a `Date` for October with 40 days will resolve to
 * November 9 because October only has 31 days. Subtracting the resolved date
 * number gives us the number of days in that month (e.g. 40 - 9 = 31).
 *
 * The 40 here is an arbitrary number. Any number greater than 31 will work,
 * but 40 is nice and round 😄.
 */
function getDaysInMonth (year: number, month: number) {
  return 40 - new Date(year, month, 40).getDate()
}

function getWeekdays (locale = 'en-US') {
  const weekdays: { long: string; narrow: string }[] = []

  const longDayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'long' })
  const narrowDayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'narrow' })

  for (let i = 0; i < 7; i++) {
    // October 2000 happens to have started on a Sunday, so its a convenient
    // starting point to get Date objects for Sunday through Saturday to
    // localize the weekday names.
    const date = new Date(2000, 9, i + 1)

    weekdays.push({
      long: longDayFormatter.format(date),
      narrow: narrowDayFormatter.format(date),
    })
  }

  return weekdays
}

function getWeeks (month: number, year: number, locale = 'en-US') {
  const firstDayOfMonth = new Date(year, month).getDay()
  const daysInMonth = getDaysInMonth(year, month)

  let day = 1
  let index = 0
  const weeks: Day[][] = []

  for (let w = 0; w < 6; w++) {
    const week: Day[] = []

    for (let d = 0; d < 7; d++) {
      if (index < firstDayOfMonth || day > daysInMonth) {
        week.push(null)
      } else {
        const date = new Date(year, month, day)
        week.push({
          ariaLabel: date.toLocaleDateString(locale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }),
          date,
          dayOfMonth: day,
        })
        day++
      }
      index++
    }

    weeks.push(week)

    if (day > daysInMonth) break
  }

  return weeks
}

const dayClassName = cva(
  'size-9 xs:size-8 inline-flex items-center justify-center rounded-md outline-transparent outline-offset-2 outline-2 focus:outline-thatch-500',
  {
    variants: {
      disabled: { true: 'body cursor-default', false: 'body-bold cursor-pointer' },
      selected: { true: null, false: null },
      today: { true: null, false: null },
    },
    compoundVariants: [
      {
        disabled: true,
        today: false,
        class: 'text-gray-400',
      },
      {
        disabled: true,
        today: true,
        class: 'text-thatch-300',
      },
      {
        disabled: false,
        selected: false,
        today: true,
        class: 'text-thatch-500 hover:bg-thatch-100 active:bg-thatch-200',
      },
      {
        disabled: false,
        selected: false,
        today: false,
        class: 'text-gray-800 hover:bg-gray-100 active:bg-gray-200',
      },
      {
        disabled: false,
        selected: true,
        today: [false, true],
        class: 'bg-thatch-500 text-white',
      },
    ]
  }
)

interface GridProps {
  date: Date | null;
  focusedDate: number;
  isDateDisabled: (date?: Date) => boolean;
  locale?: string;
  maxDate?: Date | null;
  maxYear: number;
  minDate?: Date | null;
  minYear: number;
  month: number;
  setDate: (date: Date) => void;
  setFocusedDate: (date: number) => void;
  setMonth: (month: number) => void;
  setYear: (year: number) => void;
  year: number;
}

export function Grid ({
  date,
  focusedDate,
  isDateDisabled,
  locale = navigator.language ?? 'en-US',
  maxDate,
  maxYear,
  minDate,
  minYear,
  month,
  setDate,
  setFocusedDate,
  setMonth,
  setYear,
  year,
}: GridProps) {
  const weeks = useMemo(() => getWeeks(month, year, locale), [month, year, locale])
  const weekdays = useMemo(() => getWeekdays(locale), [locale])

  const tableRef = useRef<HTMLTableElement>(null)
  const scheduleLayoutEffect = useScheduledLayoutEffect()

  function handleKeyDown (event: React.KeyboardEvent<HTMLButtonElement>) {
    if (!focusedDate) return

    let nextDate: Date | null = null

    function incrementOrDecrementDate (delta: number) {
      const daysInMonth = getDaysInMonth(year, month)
      const nextDate = new Date(year, month, focusedDate + delta)
      const minMonth = new Date(minDate?.getFullYear() ?? minYear, minDate?.getMonth() ?? 0, 1)
      const maxMonth = new Date(maxDate?.getFullYear() ?? maxYear, maxDate?.getMonth() ?? 11, daysInMonth)

      if (nextDate < minMonth) return minMonth
      if (nextDate > maxMonth) return maxMonth
      return nextDate
    }

    switch (event.key) {
      case 'ArrowLeft': {
        event.preventDefault()
        nextDate = incrementOrDecrementDate(-1)
        break
      }
      case 'ArrowRight': {
        event.preventDefault()
        nextDate = incrementOrDecrementDate(1)
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        nextDate = incrementOrDecrementDate(-7)
        break
      }
      case 'ArrowDown': {
        event.preventDefault()
        nextDate = incrementOrDecrementDate(7)
        break
      }
    }

    if (nextDate) {
      setFocusedDate(nextDate.getDate())
      setMonth(nextDate.getMonth())
      setYear(nextDate.getFullYear())

      scheduleLayoutEffect('focusNextDate', () => {
        const dateButton = tableRef.current?.querySelector<HTMLButtonElement>(
          '[data-focused="true"]'
        )

        if (dateButton) {
          dateButton.focus()
        }
      })
    }
  }

  function renderDay (day: Day, dayIndex: number) {
    const dateIsDisabled = isDateDisabled(day?.date)
    const dateIsSelected = isSameDate(day?.date, date)
    const dateIsToday = isToday(day?.date)
    const dateIsFocused = day?.dayOfMonth === focusedDate

    const ariaLabel = [
      dateIsToday && 'Today',
      day?.ariaLabel,
      dateIsSelected && 'selected',
    ]
      .filter(Boolean)
      .join(', ')

    const className = dayClassName({
      disabled: dateIsDisabled,
      selected: dateIsSelected,
      today: dateIsToday,
    })

    return (
      <td
        aria-selected={dateIsSelected || undefined}
        key={dayIndex}
        className='text-center p-0.5'
        role='gridcell'
      >
        {!!day && (
          <button
            aria-disabled={dateIsDisabled}
            aria-label={ariaLabel}
            className={className}
            data-day={day.dayOfMonth}
            data-focused={dateIsFocused}
            data-selected={dateIsSelected}
            onClick={() => {
              // Buttons for disabled dates are not actually disabled so that
              // they remain focusable by keyboard and screen reader users in
              // order to explore the calendar grid. Instead we're just
              // ignoring click event when the date is disabled.
              if (dateIsDisabled) return

              setDate(day.date)
              setFocusedDate(day.dayOfMonth)
            }}
            onKeyDown={handleKeyDown}
            tabIndex={dateIsFocused ? 0 : -1}
            type='button'
          >
            {day.dayOfMonth}
          </button>
        )}
      </td>
    )
  }

  return (
    <table className='border-collapse table-fixed' ref={tableRef} role='grid'>
      <thead aria-hidden>
        <tr>
          {weekdays.map((weekday) => (
            <th
              key={weekday.long}
              className='text-center font-normal body-sm text-gray-500'
              aria-label={weekday.long}
            >
              {weekday.narrow}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {weeks.map((week, weekIndex) => (
          <tr key={weekIndex}>{week.map(renderDay)}</tr>
        ))}
      </tbody>
    </table>
  )
}

Grid.displayName = 'Calendar.Grid'
